<template>
  <div>
    <alert :errors="errors" css_class="ma-1"></alert>
    <list-table
      class="mt-12"
      :itemList="itemList"
      :canAddUser="canAddUser"
      @on-delete-account="deleteAccount"
      @on-password-change="passwordChange"
      @on-stop-account="stopAccount"
      @on-restart-account="restartAccount"
    ></list-table>
  </div>
</template>

<script>
import ListTable from "../components/account-edits/list-table.vue";
import DateWrapper from "../logic/common/date-wrapper";
import Alert from "../components/common/alert.vue";
import AlertBuilder from "../logic/common/alert-contents";
import valueMapper from "@/logic/common/value-mapper";
import Roles from "@/logic/common/roles";
import Account from "@/logic/api/account";
import Api from "@/logic/api/api.js";
export default {
  components: {
    "list-table": ListTable,
    alert: Alert,
  },
  computed: {
    itemList() {
      return this.$store.getters.manageUserList;
    },
  },
  data() {
    this.$nextTick(() => {
      this.reloadData();
    });
    return {
      errors: [],
      canAddUser: false,
    };
  },
  watch: {
    $route() {
      this.$nextTick(() => {
        this.reloadData();
      });
    },
  },
  methods: {
    /**
     * ユーザー一覧を取得する
     */
    reloadData() {
      this.$store.commit("setWaiting", true);

      const apiRequests = [];
      const processList = [];

      // ユーザー情報を取得する
      apiRequests.push("user");
      processList.push(
        Api.call("/admin/user", {
          purpose: "USER_MANAGEMENT",
        })
      );

      // グループ情報を取得する
      apiRequests.push("group");
      processList.push(
        Api.call("/admin/group", {
          purpose: "GROUP_MANAGEMENT",
          no_detail: true,
        })
      );

      Promise.all(processList)
        .then((result) => {
          //APIから取得した結果を画面に表示する
          let userList = [];
          let groupList = [];
          if (apiRequests.includes("user")) {
            userList = result[apiRequests.indexOf("user")];
          }
          if (apiRequests.includes("group")) {
            groupList = result[apiRequests.indexOf("group")];
          }

          // しばらくお待ちくださいを閉じる
          this.$store.commit("setWaiting", false);

          try {
            // 管理対象のグループが1つでもあれば、所属ユーザーの追加を許可する
            this.canAddUser = groupList.group_list.length >= 1 ? true : false;
          } catch {
            this.canAddUser = false;
          }
          // ユーザー情報が更新されていた時のために、取得した最新のユーザー情報をログイン情報に反映する
          const loginUserUid = this.$store.getters.userId;
          const loginUser = userList.users.filter(
            (user) => user.uid == loginUserUid
          );
          Account.applyUserInfo(this.$store, { users: loginUser });
          // 画面に反映する
          this.$store.commit(
            "setManageUserList",
            userList.users.map((user) => {
              let lastLogin = "-";
              if (user.status == "inactive") {
                lastLogin = "停止中";
              } else if (user.last_sign_in_date) {
                lastLogin = new DateWrapper(
                  user.last_sign_in_date
                ).format.build(
                  (d) => `${d.date.y}/${d.date.m02}/${d.date.d02}`
                );
              }
              return {
                uid: user.uid,
                status: user.status,
                userName: user.display_name,
                mailAddress: user.email,
                group: user.permissions
                  .filter((group) => group.type == "join")
                  .map((group) => {
                    return {
                      name: group.owner.display_name,
                      color: Roles.roleToRoleColor(group.role),
                      detail: Roles.roleToRoleDescription(
                        group.owner.display_name,
                        group.role,
                        group.area
                      ),
                    };
                  }),
                permissions: user.permissions
                  .filter((group) => group.type == "permission")
                  .map((group) => {
                    return {
                      name: group.destination.display_name,
                      color: Roles.roleToRoleColor(group.role),
                      detail: Roles.roleToRoleDescription(
                        group.destination.display_name,
                        group.role,
                        group.area
                      ),
                    };
                  }),
                lastLogin: lastLogin,
                accountEdit: {
                  editable: user.control_permission.editable,
                },
              };
            })
          );
        })
        .catch((result) => {
          this.$store.commit("setWaiting", false);
          Api.resolve(result);
        });
    },
    /**
     * エラーメッセージをクリアする
     */
    clearMessage() {
      this.errors = [];
    },
    /**
     * アカウントを削除する
     */
    deleteAccount(selected) {
      const targetMap = {};
      // 送信データを作成する
      const postData = {
        delete_request: selected.map((user) => {
          targetMap[user.uid] = user.userName;
          return {
            uid: user.uid,
          };
        }),
      };
      this.clearMessage();
      this.$nextTick(() => {
        // ユーザーを削除する
        this.$store.commit("setWaiting", true);
        Api.call("/admin/user/delete", postData)
          .then((result) => {
            this.$store.commit("setWaiting", false);
            const message = valueMapper
              .map({
                success: AlertBuilder.success("削除しました"),
                failed: AlertBuilder.error("削除に失敗しました"),
              })
              .get(result.message);
            if (message) {
              this.errors.push(message);
            }
            if (result.failed) {
              result.failed.forEach((info) => {
                this.errors.push(
                  AlertBuilder.error(
                    `[${targetMap[info.uid]}]:${info.reason.jp}`
                  )
                );
              });
            }
            this.reloadData();
          })
          .catch((result) => {
            this.$store.commit("setWaiting", false);
            Api.resolve(result);
          });
      });
    },
    /**
     * パスワードを再発行する
     */
    passwordChange(selected) {
      // 送信データを作成する
      const postData = selected.map((user) => {
        return {
          email: user.mailAddress,
        };
      });
      this.clearMessage();
      this.$nextTick(() => {
        // ユーザーのパスワードを再請求する
        this.$store.commit("setWaiting", true);
        Api.repeatCall("/resetpassword", postData)
          .then(() => {
            this.$store.commit("setWaiting", false);
            // パスワードを更新した
            this.errors.push(
              AlertBuilder.success("パスワードを再発行しました")
            );
            this.reloadData();
          })
          .catch((result) => {
            this.$store.commit("setWaiting", false);
            Api.resolve(result);
          });
      });
    },
    /**
     * アカウントを停止する
     */
    stopAccount(selected) {
      const targetMap = {};
      // 送信データを作成する
      const postData = {
        update_request: selected.map((user) => {
          targetMap[user.uid] = user.userName;
          return {
            uid: user.uid,
            process: {
              status: "inactive",
            },
          };
        }),
      };
      this.clearMessage();
      this.$nextTick(() => {
        // ユーザーを停止する
        this.$store.commit("setWaiting", true);
        Api.call("/admin/user/status/update", postData)
          .then((result) => {
            this.$store.commit("setWaiting", false);
            const message = valueMapper
              .map({
                success: AlertBuilder.success("停止しました"),
                failed: AlertBuilder.error("停止に失敗しました"),
              })
              .get(result.message);
            if (message) {
              this.errors.push(message);
            }
            if (result.failed) {
              result.failed.forEach((info) => {
                this.errors.push(
                  AlertBuilder.error(
                    `[${targetMap[info.uid]}]:${info.reason.jp}`
                  )
                );
              });
            }
            this.reloadData();
          })
          .catch((result) => {
            this.$store.commit("setWaiting", false);
            Api.resolve(result);
          });
      });
    },
    /**
     * アカウントを再開する
     */
    restartAccount(selected) {
      const targetMap = {};
      // 送信データを作成する
      const postData = {
        update_request: selected.map((user) => {
          targetMap[user.uid] = user.userName;
          return {
            uid: user.uid,
            process: {
              status: "active",
            },
          };
        }),
      };
      this.clearMessage();
      this.$nextTick(() => {
        // ユーザーを再開する
        this.$store.commit("setWaiting", true);
        Api.call("/admin/user/status/update", postData)
          .then((result) => {
            this.$store.commit("setWaiting", false);
            const message = valueMapper
              .map({
                success: AlertBuilder.success("再開しました"),
                failed: AlertBuilder.error("再開に失敗しました"),
              })
              .get(result.message);
            if (message) {
              this.errors.push(message);
            }
            if (result.failed) {
              result.failed.forEach((info) => {
                this.errors.push(
                  AlertBuilder.error(
                    `[${targetMap[info.uid]}]:${info.reason.jp}`
                  )
                );
              });
            }
            this.reloadData();
          })
          .catch((result) => {
            this.$store.commit("setWaiting", false);
            Api.resolve(result);
          });
      });
    },
  },
};
</script>

<style></style>
